import { Room } from 'matrix-js-sdk/lib/models/room';
import { StateEvent } from '../state-event/state-event.service';
import { StateEvents } from 'matrix-js-sdk/lib/@types/event';
import { RoomAccessEnum, RoomHistoryVisibilityEnum, RoomJoinRuleEnum } from '../../../shared-api/roomProperties';
import { ContentHelpers, EventTimeline, EventType, JoinRule } from 'matrix-js-sdk';
import { HttpStatus, jsonError } from '../../errors/error-with-status';

export const GEMATIK_ROOMTYPE_DEFAULT_V1 = 'de.gematik.tim.roomtype.default.v1';
export const GEMATIK_STATE_ROOM_NAME = 'de.gematik.tim.room.name';
export const GEMATIK_STATE_ROOM_TOPIC = 'de.gematik.tim.room.topic';

function isGematikV1RoomType(room: Room): boolean {
  return room.getType()?.toLowerCase() === GEMATIK_ROOMTYPE_DEFAULT_V1;
}

export function changeRoomName(room: Room, newName: string): StateEvent[] {
  const stateEvents = [] as StateEvent[];
  if (newName !== room.name) {
    // Vgl. Implementierung von client.setRoomName(roomId, roomProperties.name):
    stateEvents.push({
      roomId: room.roomId,
      eventType: EventType.RoomName,
      content: { name: newName }
    });

    if (isGematikV1RoomType(room)) {
      stateEvents.push({
        roomId: room.roomId,
        eventType: GEMATIK_STATE_ROOM_NAME as keyof StateEvents,
        content: { name: newName }
      });
    }
  }

  return stateEvents;
}

export function changeRoomTheme(room: Room, newTheme?: string): StateEvent[] {
  const stateEvents = [] as StateEvent[];
  if (newTheme) {
    // Vgl. Implementierung von client.setRoomTopic(roomId, roomProperties.theme):
    stateEvents.push({
      roomId: room.roomId,
      eventType: EventType.RoomTopic,
      content: ContentHelpers.makeTopicContent(newTheme)
    });

    if (isGematikV1RoomType(room)) {
      stateEvents.push({
        roomId: room.roomId,
        eventType: GEMATIK_STATE_ROOM_TOPIC as keyof StateEvents,
        content: { topic: newTheme }
      });
    }
  }

  return stateEvents;
}

export function changeRoomAccess(room: Room, newRoomAccess?: RoomAccessEnum): StateEvent[] {
  const stateEvents = [] as StateEvent[];
  if (newRoomAccess) {
    if (room.getJoinRule() === JoinRule.Public) {
      if (newRoomAccess === 'private') {
        stateEvents.push({
          roomId: room.roomId,
          eventType: EventType.RoomJoinRules,
          content: {
            join_rule: JoinRule.Invite
          }
        });
      }
    } else if (newRoomAccess === 'public') {
      throw new Error('Cannot change private room to public');
    }
  }

  return stateEvents;
}

/**
 * Erzeugt ein m.room.history_visibility-State-Event, falls sich der Wert ändert.
 * Akzeptiert undefined und null (beides "nicht ändern", da Express null durchreichen kann).
 */
export function changeRoomHistoryVisibility(
  room: Room,
  newHistoryVisibility?: RoomHistoryVisibilityEnum | null
): StateEvent[] {
  const stateEvents: StateEvent[] = [];
  if (newHistoryVisibility === undefined || newHistoryVisibility === null) return stateEvents;

  const current = room.getHistoryVisibility() as unknown as string | undefined;
  if (current !== newHistoryVisibility) {
    stateEvents.push({
      roomId: room.roomId,
      eventType: EventType.RoomHistoryVisibility,
      content: { history_visibility: newHistoryVisibility }
    });
  }
  return stateEvents;
}

/**
 * Erzeugt ein m.room.join_rules-State-Event, falls sich der Wert ändert.
 * Hinweis: changeRoomAccess kann ebenfalls join_rules schreiben; eine explizite joinRule-Angabe
 * im Body hat Vorrang und überschreibt den dort erzeugten Default.
 *
 * Sonderregel: Ein bestehender, nicht-öffentlicher Raum darf nachträglich NICHT auf "public"
 * umgestellt werden (TI-Vorgabe). Wir signalisieren das mit einem strukturierten 400-Fehler,
 * den handleError als HTTP 400 ausliefert.
 */
export function changeRoomJoinRule(room: Room, newJoinRule?: RoomJoinRuleEnum | null): StateEvent[] {
  const stateEvents: StateEvent[] = [];
  if (newJoinRule === undefined || newJoinRule === null) return stateEvents;

  const current = room.getJoinRule() as unknown as string | undefined;
  if (current === newJoinRule) return stateEvents;

  if (newJoinRule === 'public' && current !== JoinRule.Public) {
    throw jsonError(
      'ChangeJoinRuleNotAllowedError',
      HttpStatus.BadRequest,
      'Cannot change a non-public room to public via join_rule'
    );
  }

  stateEvents.push({
    roomId: room.roomId,
    eventType: EventType.RoomJoinRules,
    content: { join_rule: newJoinRule }
  });
  return stateEvents;
}

/**
 * m.federate ist laut Matrix-Spec immutable (nur im m.room.create setzbar). Eine Änderung
 * würde vom Homeserver still ignoriert. Statt einen falschen Erfolg zu signalisieren,
 * werfen wir bei abweichendem Wunschwert. Gleicher Wert → no-op.
 */
export function changeRoomIsFederated(room: Room, newIsFederated?: boolean | null): StateEvent[] {
  if (newIsFederated === undefined || newIsFederated === null) return [];

  const createEvent = room.getLiveTimeline().getState(EventTimeline.FORWARDS)?.getStateEvents(EventType.RoomCreate, '');
  const content = createEvent?.getContent() as { 'm.federate'?: boolean };
  const current = content?.['m.federate'] ?? true;
  if (current !== newIsFederated) {
    throw new Error('isFederated is immutable after room creation and cannot be changed');
  }
  return [];
}

/**
 * Verschlüsselung darf nachträglich aktiviert, aber nicht mehr entfernt werden (TI-Vorgabe).
 * Eine Anforderung, einen verschlüsselten Raum zu entschlüsseln, wird mit HTTP 400 abgelehnt.
 * null und undefined werden als "nicht ändern" behandelt.
 */
export function changeRoomEncryption(room: Room, newIsEncrypted?: boolean | null): StateEvent[] {
  if (newIsEncrypted === undefined || newIsEncrypted === null) return [];

  const isRoomEncrypted = room.hasEncryptionStateEvent();

  if (newIsEncrypted && !isRoomEncrypted) {
    return [
      {
        roomId: room.roomId,
        eventType: EventType.RoomEncryption,
        content: { algorithm: 'm.megolm.v1.aes-sha2' }
      }
    ];
  }
  if (!newIsEncrypted && isRoomEncrypted) {
    throw jsonError(
      'RemoveEncryptionNotAllowedError',
      HttpStatus.BadRequest,
      'You can not take off encryption from an encrypted room'
    );
  }
  return [];
}
